import { useCallback } from 'react';

import { Icons } from '@cast/design-system';
import {
  useToggleWorkloadOptimization,
  useToggleWorkloadOptimizationChangeDialog,
} from '@cast/split-me/features/cluster/workload-optimization';
import type { PreparedWorkload } from '@cast/split-me/features/workload-optimization';

type UseInventoryWorkloadOptimizationArgs = {
  workload: PreparedWorkload;
};

type UseInventoryWorkloadOptimizationReturn = {
  optimizationType: string;
  isOverridden: boolean;
  onOverride: () => void;
  onRevert: () => void;
};

export const useInventoryWorkloadOptimization = ({
  workload,
}: UseInventoryWorkloadOptimizationArgs): UseInventoryWorkloadOptimizationReturn => {
  const { openOptimizationChangeDialog } =
    useToggleWorkloadOptimizationChangeDialog();
  const { revertToPolicy, toggleManagementOption } =
    useToggleWorkloadOptimization({
      workloadId: workload.id,
    });

  const { optimizationTypeState, name } = workload;

  const onOverride = useCallback(() => {
    openOptimizationChangeDialog({
      workloads: [workload],
      action: 'override',
      initialValue: optimizationTypeState.type,
      title: 'Override optimization type',
      titleName: name,
      icon: <Icons.LinkBreak size={16} weight="bold" />,
      confirmBtnLabel: 'Apply override',
      onApply: ({ appliedType }) => {
        toggleManagementOption({
          typeToApply: appliedType,
        });
      },
    });
  }, [
    workload,
    optimizationTypeState.type,
    name,
    openOptimizationChangeDialog,
    toggleManagementOption,
  ]);

  const onRevert = useCallback(() => {
    openOptimizationChangeDialog({
      workloads: [workload],
      action: 'revertToPolicy',
      initialValue: optimizationTypeState.type,
      title: 'Revert optimization',
      titleName: name,
      icon: <Icons.Link size={16} weight="bold" />,
      confirmBtnLabel: 'Apply override',
      onApply: revertToPolicy,
    });
  }, [
    workload,
    optimizationTypeState.type,
    name,
    openOptimizationChangeDialog,
    revertToPolicy,
  ]);

  return {
    optimizationType: optimizationTypeState.type,
    isOverridden: optimizationTypeState.isOverridden,
    onOverride,
    onRevert,
  };
};
